import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, SearchX, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MovieCard, MovieCardSkeleton } from "../components/MovieCard";
import { ErrorState } from "../components/ErrorState";
import { AdSlot } from "../components/AdSlot";
import { api, errorMessage } from "../lib/api";
import { useFetch } from "../lib/useFetch";

const TABS = [
  { id: "trending", label: "Trending", url: "/trending", params: { window: "week" } },
  { id: "popular", label: "Popular", url: "/popular" },
  { id: "top_rated", label: "Top Rated", url: "/top_rated" },
];

const MEDIA = {
  movie: { base: "/movies", genres: "/genres", eyebrow: "The Movie Catalog", other: { label: "TV Series", path: "/tv" } },
  tv: { base: "/tv", genres: "/tv/genres", eyebrow: "The TV Series Catalog", other: { label: "Movies", path: "/movies" } },
};

const buildRequest = (base, tab, genre, q) => {
  if (q) return [`${base}/search`, { q, ...(genre !== "all" && { genre }) }];
  if (genre !== "all") return [`${base}/discover`, { genre, sort: tab }];
  const t = TABS.find((x) => x.id === tab);
  return [`${base}${t.url}`, t.params || {}];
};

export default function Catalog({ media = "movie" }) {
  const cfg = MEDIA[media];
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState("trending");
  const [genre, setGenre] = useState("all");
  const [movies, setMovies] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const genres = useFetch(cfg.genres);

  useEffect(() => setInput(q), [q]);

  const load = useCallback(
    (p) => {
      const [url, extra] = buildRequest(cfg.base, tab, genre, q);
      setStatus(p === 1 ? "loading" : "more");
      api
        .get(url, { params: { ...extra, page: p } })
        .then(({ data }) => {
          setMovies((prev) => {
            if (p === 1) return data.results;
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...data.results.filter((m) => !seen.has(m.id))];
          });
          setPage(p);
          setTotalPages(data.total_pages);
          setStatus("idle");
        })
        .catch((e) => {
          setError(errorMessage(e));
          setStatus(p === 1 ? "error" : "idle");
        });
    },
    [cfg.base, tab, genre, q]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const submit = (e) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const heading = q ? `Results for “${q}”` : TABS.find((t) => t.id === tab).label;

  return (
    <div data-testid="catalog-page" className="mx-auto max-w-7xl px-5 pb-10 pt-28 md:px-10 md:pt-36">
      <p className="eyebrow text-[#D4AF37]">{cfg.eyebrow}</p>
      <h1 data-testid="catalog-heading" className="mt-3 font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">{heading}</h1>
      {q && (
        <Link to={`${cfg.other.path}?q=${encodeURIComponent(q)}`} data-testid="search-other-media-link" className="mt-3 inline-block text-sm text-neutral-400 underline-offset-4 transition-colors duration-300 hover:text-[#D4AF37] hover:underline">
          Search {cfg.other.label} for “{q}” instead
        </Link>
      )}

      <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <form onSubmit={submit} className="relative w-full lg:max-w-md" data-testid="catalog-search-form">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search by title..."
            data-testid="search-input"
            className="h-12 w-full rounded-full border border-white/10 bg-[#121212] pl-11 pr-24 text-sm text-white placeholder:text-neutral-500 transition-colors duration-300 focus:border-[#D4AF37]/70 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
          />
          {q && (
            <button type="button" onClick={() => setParams({})} data-testid="search-clear-btn" aria-label="Clear search" className="absolute right-[5.5rem] top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          )}
          <button type="submit" data-testid="search-submit-btn" className="btn-gold absolute right-1.5 top-1/2 h-9 -translate-y-1/2 rounded-full px-5 text-xs font-semibold uppercase tracking-[0.12em]">
            Search
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-3">
          <div className={`flex rounded-full border border-white/10 bg-[#121212] p-1 ${q ? "pointer-events-none opacity-40" : ""}`} data-testid="category-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`category-tab-${t.id}`}
                data-active={tab === t.id}
                className={`rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] transition-colors duration-300 sm:px-5 ${
                  tab === t.id ? "bg-[#D4AF37] text-black" : "text-neutral-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger data-testid="genre-filter" className="h-11 w-44 rounded-full border-white/10 bg-[#121212] text-sm text-white focus:ring-[#D4AF37]/40">
              <SelectValue placeholder="All genres" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#121212] text-white">
              <SelectItem value="all" data-testid="genre-option-all">All genres</SelectItem>
              {(genres.data?.genres || []).map((g) => (
                <SelectItem key={g.id} value={String(g.id)} data-testid={`genre-option-${g.id}`}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <AdSlot name="catalog_top" className="!px-0 pb-0 pt-10" />
      <div className="mt-12">
        {status === "error" ? (
          <ErrorState message={error} onRetry={() => load(1)} testId="catalog-error" />
        ) : status === "loading" ? (
          <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: 10 }).map((_, i) => <MovieCardSkeleton key={i} />)}
          </div>
        ) : movies.length === 0 ? (
          <div data-testid="no-results" className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-[#121212] px-6 py-20 text-center">
            <SearchX className="h-10 w-10 text-[#D4AF37]" />
            <h2 className="font-display text-2xl text-white">No results</h2>
            <p className="max-w-md text-sm text-neutral-400">We couldn't find any {media === "tv" ? "series" : "movies"} matching your search. Try another title or a different genre.</p>
          </div>
        ) : (
          <div data-testid="movie-grid" className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {movies.map((m, i) => (
              <div key={m.id} className="fade-up" style={{ animationDelay: `${(i % 20) * 30}ms` }}>
                <MovieCard movie={m} />
              </div>
            ))}
          </div>
        )}

        {status !== "error" && status !== "loading" && movies.length > 0 && page < totalPages && (
          <div className="mt-14 flex justify-center">
            <button
              type="button"
              onClick={() => load(page + 1)}
              disabled={status === "more"}
              data-testid="load-more-btn"
              className="btn-outline-gold inline-flex items-center gap-2 rounded-full px-10 py-3.5 text-sm font-medium uppercase tracking-[0.14em] disabled:opacity-60"
            >
              {status === "more" && <Loader2 className="h-4 w-4 animate-spin" />}
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
